import React, { useMemo } from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import { propsToDom, propsToLink } from 'core/helpers';
import { Panel } from 'components/Panel';
import { rem } from 'styles/utils';
import { Title3 } from 'styles/typography';
import { Button } from 'components/Button';
import { Image } from 'components/Image';
import media from 'styles/media';
import { Link } from 'components/Link';

const RelatedsComponent = ({ primary, items, slice_type, next_slice_type, ...others }) => {
  const { background } = primary;
  const projects = useMemo(() => items.filter(({ item_title }) => item_title?.text), [items]);

  return (
    <Panel {...propsToDom(others)} id={slice_type} nextId={next_slice_type} background={background}>
      {projects.map(({ image, button, button_text, button_color, item_title }, index) => {
        const propsLink = button?.url ? propsToLink(button) : {};
        const hasImage = !!image?.url;
        const hasButton = !!button?.url && !!button_text;
        const hasTitle = !!item_title?.text;

        return (
          <Item key={index} as={hasButton ? Link : 'div'} {...propsLink}>
            {hasImage && <Image {...image} />}
            <Content>
              {hasTitle && <Title3>{item_title.text}</Title3>}
              {hasButton && (
                <Button icon="arrow-right" color={button_color}>
                  {button_text}
                </Button>
              )}
            </Content>
          </Item>
        );
      })}
    </Panel>
  );
};

const Item = styled.div`
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    display: block;
    padding-bottom: ${(900 / 720) * 100}%;
  }

  ${Image} {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transition: transform 0.7s ${({ theme }) => theme.easings.principle};
  }

  @media (hover: hover) and (pointer: fine) {
    &:not(div):hover {
      ${Image} {
        transform: scale(1.05);
      }
    }
  }
`;

const Content = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  padding: var(--margin);

  ${Title3} {
    margin-bottom: auto;
  }

  ${Button} {
    align-self: flex-start;
    margin-top: ${rem(32)};
  }
`;

export const Relateds = styled(RelatedsComponent)`
  display: grid;
  grid-template-columns: repeat(2, minmax(10px, 1fr));
  pointer-events: none;

  a,
  button {
    pointer-events: auto;
  }

  ${media.mobile`
    grid-template-columns: 1fr;
  `}
`;

export const query = graphql`
  fragment SliceRelatedsHome on PrismicHomeDataBodyRelateds {
    id
    slice_type
    primary {
      background
    }
    items {
      item_title {
        text
      }
      button {
        url
        target
      }
      button_text
      button_color
      image {
        url
        alt
        dimensions {
          width
          height
        }
      }
    }
  }

  fragment SliceRelatedsPage on PrismicPageDataBodyRelateds {
    id
    slice_type
    primary {
      background
    }
    items {
      item_title {
        text
      }
      button {
        url
        target
      }
      button_text
      button_color
      image {
        url
        alt
        dimensions {
          width
          height
        }
      }
    }
  }
`;
